import { useEffect, useState } from "react";
import { Link2 } from "lucide-react";
import { toast } from "@/lib/toast";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useLibraryUrlPattern } from "@/hooks/useZoteroSource";

const PRESETS = [
  {
    name: "EZproxy",
    pattern:
      "https://login.ezproxy.example.edu/login?url=https://doi.org/{DOI}",
  },
  {
    name: "OpenURL",
    pattern:
      "https://library.example.edu/sfx?url_ver=Z39.88-2004&rft.doi={DOI}",
  },
  {
    name: "LibKey",
    pattern: "https://libkey.io/articles/?doi={DOI}&prefix=example",
  },
];
const SAMPLE_DOI = "10.1234/example";

export function LibraryUrlPattern() {
  const { query, setMutation } = useLibraryUrlPattern();
  const [value, setValue] = useState("");
  const [testValue, setTestValue] = useState<string | null>(null);

  useEffect(() => {
    if (query.data?.pattern && !value) setValue(query.data.pattern);
  }, [query.data, value]);

  const save = async () => {
    await setMutation.mutateAsync(value || null);
    toast.success(value ? "Library URL pattern saved" : "Library URL pattern cleared");
  };

  const test = () => {
    if (!value) return setTestValue(null);
    setTestValue(value.replace("{DOI}", encodeURIComponent(SAMPLE_DOI)));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Link2 className="size-4" /> Institutional library link
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Use <code className="font-mono">{"{DOI}"}</code> as a placeholder. Powers
          the &ldquo;Get PDF&rdquo; button on paper detail.
        </p>
        <Input
          placeholder="https://login.ezproxy.example.edu/login?url=https://doi.org/{DOI}"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <div className="flex flex-wrap gap-1">
          {PRESETS.map((p) => (
            <button
              key={p.name}
              type="button"
              onClick={() => setValue(p.pattern)}
              className="rounded border border-input bg-muted px-2 py-0.5 text-xs hover:bg-muted/70"
            >
              {p.name}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={setMutation.isPending}>
            Save
          </Button>
          <Button size="sm" variant="ghost" onClick={test} disabled={!value}>
            Test
          </Button>
        </div>
        {testValue && (
          <p className="break-all rounded bg-muted px-2 py-1 font-mono text-xs">
            {testValue}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
